import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../api';
import { Loading, ErrorState, fmtDate } from '../../components/ui';
import { Kpis, Panel } from '../../components/charts';
import { METHOD_LABEL, ENTITLEMENT_LABEL } from './Dashboard';

/* ============================================================
   Satu analisis: jendela, atribusi, mitigasi, penerbitan.

   Kolom "belum dijelaskan" ada di setiap baris jendela dan tidak
   bisa disembunyikan. Sisa yang tidak dikaitkan ke peristiwa mana
   pun adalah bagian paling jujur dari laporan ini.
   ============================================================ */

type Attribution = {
  id: string;
  days: number;
  entitlement: string;
  concurrent: boolean;
  reasoning: string | null;
  event: { id: string; eventNo: string; title: string; cause: string; responsibility: string };
};

type Window = {
  id: string;
  windowNo: number;
  start: string;
  end: string;
  slipDays: number;
  completionOpening: string | null;
  completionClosing: string | null;
  note: string | null;
  unexplained: number;
  drivingActivity: { activityCode: string; name: string } | null;
  attributions: Attribution[];
};

type Modelled = {
  method: string;
  basis: 'MEASURED' | 'MODELLED';
  from: string | null;
  to: string | null;
  days: number;
  concurrentDays: number;
  caveat: string;
  events: { eventNo: string; title: string; days: number; concurrent: boolean }[];
  sanity: { overshootDays: number; note: string | null };
};

type Variance = {
  baseline: string;
  asBuilt: string;
  basis: string;
  completionPlanned: string | null;
  completionActual: string | null;
  totalSlip: number;
  drivingActivityCode: string | null;
  forecastRows: number;
  addedRows: number;
  truncated: number;
  rows: {
    activityCode: string;
    plannedFinish: string | null;
    actualFinish: string | null;
    slipDays: number;
    isCritical: boolean;
    forecast: boolean;
    added: boolean;
  }[];
};

type Detail = {
  modelled: Modelled | null;
  analysis: {
    id: string;
    title: string;
    method: string;
    status: string;
    limitations: string | null;
    concurrencyRule: string | null;
    issuedAt: string | null;
    preparedBy: { name: string };
    spec: { strength: string; limitations: string };
  };
  windows: Window[];
  totals: {
    excusableCompensable: number;
    excusableOnly: number;
    nonExcusable: number;
    total: number;
    concurrentDays: number;
    totalSlip: number;
    unexplained: number;
  };
  mitigations: {
    id: string;
    action: string;
    recoveryDays: number;
    cost: string | number;
    ownerName: string | null;
    status: string;
  }[];
  mitigationTotals: { agreedDays: number; proposedDays: number; agreedCost: number; proposedCost: number };
  canIssue: { ok: boolean; missing: string[] };
};

type EventRow = { id: string; eventNo: string; title: string };

export function Analysis({ projectId }: { projectId: string }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState<Detail | null>(null);
  const [variance, setVariance] = useState<Variance | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [d, e] = await Promise.all([
        api.get<Detail>(`/delay/analyses/${id}`),
        api.get<{ events: EventRow[] }>(`/delay/events?projectId=${projectId}`),
      ]);
      setData(d);
      setEvents(e.events);
      // Varians dihitung saat diminta dan hanya untuk satu metode.
      if (d.analysis.method === 'AS_PLANNED_VS_AS_BUILT') {
        setVariance(await api.get<Variance>(`/delay/analyses/${id}/variance`).catch(() => null));
      } else {
        setVariance(null);
      }
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not load the analysis.');
    }
  }, [id, projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function build() {
    setBusy(true);
    try {
      const r = await api.post<{ windows: number; totalSlip: number }>(`/delay/analyses/${id}/windows`, {});
      setNotice(`${r.windows} jendela terbentuk, total slip ${r.totalSlip} hari.`);
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal membentuk jendela.');
    } finally {
      setBusy(false);
    }
  }

  async function attribute(windowId: string) {
    if (events.length === 0) {
      setNotice('Belum ada peristiwa untuk dikaitkan. Catat satu lebih dulu.');
      return;
    }
    const eventNo = window.prompt(
      `Nomor peristiwa — pilihan: ${events.map((e) => e.eventNo).join(', ')}`,
    );
    const ev = events.find((e) => e.eventNo === eventNo?.trim());
    if (!ev) return;

    const days = Number(window.prompt('Berapa hari dikaitkan ke peristiwa ini?') ?? '');
    if (!Number.isFinite(days)) return;

    const ent = window.prompt(
      'Entitlement — C untuk waktu & biaya, W untuk waktu saja, N untuk tidak berhak:',
      'C',
    );
    const entitlement =
      ent?.toUpperCase() === 'C'
        ? 'EXCUSABLE_COMPENSABLE'
        : ent?.toUpperCase() === 'W'
          ? 'EXCUSABLE_ONLY'
          : 'NON_EXCUSABLE';

    const concurrent = window.confirm('Berjalan bersamaan dengan keterlambatan pihak lain?');
    const reasoning = concurrent
      ? window.prompt('Alasan penandaan konkurensi — wajib, dan akan diperiksa pihak lawan:')
      : (window.prompt('Alasan (opsional):') ?? '');
    if (concurrent && !reasoning?.trim()) return;

    setBusy(true);
    try {
      await api.post(`/delay/windows/${windowId}/attributions`, {
        eventId: ev.id,
        days,
        entitlement,
        concurrent,
        reasoning,
      });
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal mengaitkan.');
    } finally {
      setBusy(false);
    }
  }

  async function addMitigation() {
    const action = window.prompt('Tindakan pemulihan:');
    if (!action?.trim()) return;
    const recoveryDays = Number(window.prompt('Berapa hari bisa dipulihkan?', '0') ?? '0');
    const cost = Number(window.prompt('Biayanya?', '0') ?? '0');
    setBusy(true);
    try {
      await api.post(`/delay/analyses/${id}/mitigations`, { action, recoveryDays, cost });
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal.');
    } finally {
      setBusy(false);
    }
  }

  async function issue() {
    const d = data!;
    let limitations = d.analysis.limitations ?? '';
    let concurrencyRule = d.analysis.concurrencyRule ?? '';

    if (!limitations.trim()) {
      limitations = window.prompt('Kelemahan metode ini — wajib sebelum terbit:', d.analysis.spec.limitations) ?? '';
      if (!limitations.trim()) return;
    }
    if (d.totals.concurrentDays > 0 && !concurrencyRule.trim()) {
      concurrencyRule =
        window.prompt('Aturan konkurensi yang dipakai — wajib karena ada hari konkuren:') ?? '';
      if (!concurrencyRule.trim()) return;
    }

    setBusy(true);
    try {
      await api.post(`/delay/analyses/${id}/issue`, { limitations, concurrencyRule });
      setNotice('Analisis terbit.');
      await load();
    } catch (e) {
      setNotice(e instanceof ApiError ? e.message : 'Gagal menerbitkan.');
    } finally {
      setBusy(false);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!data) return <Loading rows={5} />;

  const a = data.analysis;
  const editable = a.status !== 'ISSUED';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{a.title}</h1>
          <p>
            {METHOD_LABEL[a.method] ?? a.method} · disusun {a.preparedBy.name}
            {a.issuedAt && ` · terbit ${fmtDate(a.issuedAt)}`}
          </p>
        </div>
        <div className="filters" style={{ marginBottom: 0 }}>
          <span className={`chip ${a.status === 'ISSUED' ? 'good' : ''}`}>{a.status}</span>
          {editable && data.windows.length === 0 && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={build}>
              Bentuk jendela
            </button>
          )}
          {editable && (
            <button className="btn btn-ghost btn-sm" disabled={busy} onClick={addMitigation}>
              + Mitigasi
            </button>
          )}
          {editable && data.windows.length > 0 && (
            <button className="btn btn-primary btn-sm" disabled={busy} onClick={issue}>
              Terbitkan
            </button>
          )}
          <a
            className="btn btn-ghost btn-sm"
            href={api.url(`/delay/analyses/${id}/report.pdf`)}
            target="_blank"
            rel="noreferrer"
          >
            Unduh PDF
          </a>
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/')}>
            Kembali
          </button>
        </div>
      </div>

      {notice && <div className="banner">{notice}</div>}

      {editable && !data.canIssue.ok && data.windows.length > 0 && (
        <div className="banner">
          Belum bisa diterbitkan. Belum ada: {data.canIssue.missing.join(', ')}.
        </div>
      )}

      <Kpis
        items={[
          { label: 'Total slip', value: `${data.totals.totalSlip} hari` },
          { label: 'Dikaitkan', value: `${data.totals.total} hari` },
          {
            label: 'Belum dijelaskan',
            value: `${data.totals.unexplained} hari`,
            tone: data.totals.unexplained > 0 ? 'warn' : 'good',
          },
          {
            label: 'Konkuren',
            value: `${data.totals.concurrentDays} hari`,
            tone: data.totals.concurrentDays > 0 ? 'warn' : undefined,
          },
        ]}
      />

      {data.modelled && (
        <Panel
          title={
            data.modelled.method === 'COLLAPSED_AS_BUILT'
              ? 'Collapsed as-built — seandainya peristiwa ini tidak terjadi'
              : 'Impacted as-planned — seandainya peristiwa ini disisipkan ke baseline'
          }
          note="Angka DIMODELKAN, bukan diukur. Bacalah bersama caveat di bawahnya."
          wide
        >
          <Kpis
            items={[
              {
                label: data.modelled.method === 'COLLAPSED_AS_BUILT' ? 'As-built selesai' : 'Baseline selesai',
                value: data.modelled.from ? fmtDate(data.modelled.from) : '—',
              },
              {
                label: data.modelled.method === 'COLLAPSED_AS_BUILT' ? 'Seharusnya selesai' : 'Menjadi selesai',
                value: data.modelled.to ? fmtDate(data.modelled.to) : '—',
              },
              {
                label: data.modelled.method === 'COLLAPSED_AS_BUILT' ? 'Hari dihapus' : 'Hari disisipkan',
                value: `${data.modelled.days} hari`,
              },
              {
                label: 'Di antaranya konkuren',
                value: `${data.modelled.concurrentDays} hari`,
                tone: data.modelled.concurrentDays > 0 ? 'warn' : undefined,
              },
            ]}
          />

          {data.modelled.events.length > 0 && (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Peristiwa</th>
                    <th>Hari</th>
                    <th>Konkuren</th>
                  </tr>
                </thead>
                <tbody>
                  {data.modelled.events.map((e) => (
                    <tr key={e.eventNo} className="no-click">
                      <td className="num">
                        {e.eventNo}
                        <div className="hint">{e.title}</div>
                      </td>
                      <td>{e.days}</td>
                      <td>{e.concurrent ? <span className="chip warn">ya</span> : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <p className="panel-foot">{data.modelled.caveat}</p>
          {data.modelled.sanity.note && (
            <div className="banner">{data.modelled.sanity.note}</div>
          )}
        </Panel>
      )}

      {variance && (
        <Panel
          title="Varians per aktivitas"
          note={`${variance.baseline} → ${variance.asBuilt}. Angka ini TERUKUR dari dua programme, bukan dimodelkan.`}
          wide
        >
          <Kpis
            items={[
              {
                label: 'Rencana selesai',
                value: variance.completionPlanned ? fmtDate(variance.completionPlanned) : '—',
              },
              {
                label: 'Nyatanya selesai',
                value: variance.completionActual ? fmtDate(variance.completionActual) : '—',
              },
              { label: 'Slip total', value: `${variance.totalSlip} hari` },
              {
                label: 'Masih perkiraan',
                value: `${variance.forecastRows} aktivitas`,
                note: variance.forecastRows > 0 ? 'belum selesai; tanggalnya rencana' : undefined,
                tone: variance.forecastRows > 0 ? 'warn' : undefined,
              },
            ]}
          />

          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Aktivitas</th>
                  <th>Rencana</th>
                  <th>Nyatanya</th>
                  <th>Geser</th>
                  <th>Catatan</th>
                </tr>
              </thead>
              <tbody>
                {variance.rows.slice(0, 40).map((r) => (
                  <tr key={r.activityCode} className="no-click">
                    <td className="num">{r.activityCode}</td>
                    <td>{r.plannedFinish ? fmtDate(r.plannedFinish) : '—'}</td>
                    <td>{r.actualFinish ? fmtDate(r.actualFinish) : '—'}</td>
                    <td className={r.slipDays > 0 ? 'neg' : r.slipDays < 0 ? 'pos' : ''}>
                      {r.slipDays === 0 ? '—' : `${r.slipDays > 0 ? '+' : ''}${r.slipDays} h`}
                    </td>
                    <td className="hint">
                      {r.isCritical && <span className="chip crit">kritis</span>}{' '}
                      {r.forecast && <span className="chip warn">perkiraan</span>}{' '}
                      {r.added && <span className="chip">tambahan</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {(variance.rows.length > 40 || variance.truncated > 0) && (
            <p className="panel-foot">
              Menampilkan 40 dari {variance.rows.length + variance.truncated} baris, yang paling
              mundur lebih dulu.
            </p>
          )}
        </Panel>
      )}

      <Panel
        title="Kelemahan metode"
        note="Wajib masuk ke laporan. Analisis yang tidak menyatakan metodenya adalah analisis yang tidak bisa diperiksa."
        wide
      >
        <p>{a.limitations ?? a.spec.limitations}</p>
        {a.concurrencyRule && (
          <p className="panel-foot">Aturan konkurensi: {a.concurrencyRule}</p>
        )}
      </Panel>

      {data.windows.map((w) => (
        <Panel
          key={w.id}
          title={`Jendela ${w.windowNo} · ${fmtDate(w.start)} – ${fmtDate(w.end)}`}
          note={w.note ?? undefined}
          wide
        >
          <div className="filters">
            <span className={`chip ${w.slipDays > 14 ? 'crit' : w.slipDays > 0 ? 'warn' : 'good'}`}>
              Slip {w.slipDays} hari
            </span>
            {w.drivingActivity && (
              <span className="chip blue" title={w.drivingActivity.name}>
                Pendorong: {w.drivingActivity.activityCode}
              </span>
            )}
            <span className={`chip ${w.unexplained !== 0 ? 'warn' : ''}`}>
              Belum dijelaskan {w.unexplained} hari
            </span>
            {w.completionOpening && w.completionClosing && (
              <span className="hint">
                selesai {fmtDate(w.completionOpening)} → {fmtDate(w.completionClosing)}
              </span>
            )}
            {editable && (
              <button className="btn btn-ghost btn-sm" disabled={busy} onClick={() => attribute(w.id)}>
                Kaitkan peristiwa
              </button>
            )}
          </div>

          {w.attributions.length > 0 && (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Peristiwa</th>
                    <th>Hari</th>
                    <th>Entitlement</th>
                    <th>Konkuren</th>
                    <th>Alasan</th>
                  </tr>
                </thead>
                <tbody>
                  {w.attributions.map((x) => (
                    <tr key={x.id} className="no-click">
                      <td className="num">
                        {x.event.eventNo}
                        <div className="hint">{x.event.title}</div>
                      </td>
                      <td>{x.days}</td>
                      <td>{ENTITLEMENT_LABEL[x.entitlement] ?? x.entitlement}</td>
                      <td>{x.concurrent ? <span className="chip warn">ya</span> : '—'}</td>
                      <td className="hint">{x.reasoning ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      ))}

      {data.mitigations.length > 0 && (
        <Panel
          title="Rencana mitigasi"
          note="Dibangun dari pemulihan yang benar-benar tersedia, bukan yang kebetulan nyaman."
          wide
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Tindakan</th>
                  <th>Pemulihan</th>
                  <th>Biaya</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {data.mitigations.map((m) => (
                  <tr key={m.id} className="no-click">
                    <td>{m.action}</td>
                    <td>{m.recoveryDays} hari</td>
                    <td>{Number(m.cost).toLocaleString('id-ID')}</td>
                    <td>
                      <span className={`chip ${m.status === 'ABANDONED' ? '' : m.status === 'PROPOSED' ? 'warn' : 'good'}`}>
                        {m.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="panel-foot">
            Disepakati {data.mitigationTotals.agreedDays} hari; baru diusulkan{' '}
            {data.mitigationTotals.proposedDays} hari. Rencana yang menjumlahkan usulan sebagai
            kepastian akan meleset persis sebesar usulan yang ditolak.
          </p>
        </Panel>
      )}
    </>
  );
}
